import React, { useCallback, useEffect } from 'react'
import '../assets/css/style.css'
import info from '../assets/img/icon/info.svg'
import guide_reversal_1 from '../assets/img/torikuzushi_simulation/guide_reversal_1.svg'
import guide_reversal_1_sp from '../assets/img/torikuzushi_simulation/guide_reversal_1_sp.svg'
import { Modal } from './Modal'

interface FinancialCalcReversalGuideModalProps {
  isShow: boolean
  setShow: React.Dispatch<React.SetStateAction<boolean>>
}

// eslint-disable-next-line max-len
export const FinancialCalcReversalGuideModal: React.FC<FinancialCalcReversalGuideModalProps> =
  React.memo((props) => {
    const { isShow, setShow } = props
    useEffect(() => {
      if (isShow) {
        document.body.setAttribute('style', 'overflow: hidden;')
      } else {
        document.body.removeAttribute('style')
      }

      return () => document.body.removeAttribute('style')
    }, [isShow])

    const handleHide = useCallback(() => {
      setShow(false)
    }, [setShow])

    return (
      <React.Fragment>
        {isShow && (
          <Modal className="c-modal">
            <div className="c-modal__content reversal_guide">
              <div className="c-modal__head">
                <span className="c-modal__head__icon">
                  <img src={info} alt="" />
                </span>
                <h2 className="c-modal__head__title">
                  取り崩しシミュレーション
                  <br className="spOnly" /> 使い方ガイド
                </h2>
                <span className="c-modal__head__close" onClick={handleHide}></span>
              </div>
              <div className="c-modal__body">
                <div className="c-modal__lead">
                  <p>
                    定額・定率の取り崩しにより<br className="md_only"></br>
                    あなたの資産が<br className="ss_only"></br>
                    どのように変化するか、
                    <br></br>年齢ごとのシミュレーションを
                    <br className="md_only"></br>確認できます
                  </p>
                </div>
                <h3 className="c-heading-1">操作方法</h3>
                <p className="u-mb1em">
                  以下の表に記載の項目を入力・選択し、「計算する」をクリック（タップ）してください。
                  <br></br>
                  入力値が範囲外の場合、エラーが表示されます。範囲内の数値を入力してください。
                </p>

                <table className="c-table">
                  <tbody>
                    <tr>
                      <th colSpan={2}>項目</th>
                      <td className="center">入力値</td>
                    </tr>
                    <tr>
                      <th colSpan={2}>取り崩し開始時点の保有資産</th>
                      <td>1～99,999 万円</td>
                    </tr>
                    <tr>
                      <th colSpan={2}>取り崩し開始年齢</th>
                      <td>20～99 歳</td>
                    </tr>
                    <tr>
                      <th colSpan={2}>取り崩し頻度</th>
                      <td>「毎月」「隔月」どちらか一方を選択</td>
                    </tr>
                    <tr>
                      <th rowSpan={3} className="no-border middle">
                        取り崩し方法
                      </th>
                      <th className="no-border"></th>
                      <td>
                        「定率」「定額」の<br></br>いずれかまたは両方に入力
                      </td>
                    </tr>
                    <tr>
                      <th>
                        定率（年間）<sup>*</sup>
                      </th>
                      <td>0～15.0 %</td>
                    </tr>
                    <tr>
                      <th>
                        定額（1回あたり）<sup>*</sup>
                      </th>
                      <td>0～999.9 万円</td>
                    </tr>
                    <tr>
                      <th colSpan={2}>
                        運用した場合のリターン（年率）<sup>*</sup>
                      </th>
                      <td>0～30.0 %</td>
                    </tr>
                  </tbody>
                </table>
                <p className="u-mb1em">* 小数点一桁まで有効</p>
                <p>
                  シミュレーションの運用期間中において表示可能な保有資産残高の上限は、99,999,999万円です。
                </p>
                <p className="u-mb1em">
                  シミュレーションの結果、保有資産残高が上限を超える場合、エラーが表示されます。入力内容を変更してください。
                </p>
                <h3 className="c-heading-1">シミュレーション結果</h3>
                <p className="u-mb1em">シミュレーション結果の出力項目は以下の通りです。</p>
                <table className="c-table">
                  <tbody>
                    <tr>
                      <th colSpan={2}>
                        取り崩し額<sup>*1</sup>
                      </th>
                      <td>「毎月」「隔月」いずれかの取り崩し額</td>
                    </tr>
                    <tr>
                      <th colSpan={2}>
                        保有資産残高<sup>*1</sup>
                      </th>
                      <td>取り崩しをした後の保有資産残高</td>
                    </tr>
                    <tr>
                      <th rowSpan={2}>
                        資産寿命<sup>*2</sup>
                      </th>
                      <th>定率取り崩し</th>
                      <td>保有資産が1円未満となる時点における年齢</td>
                    </tr>
                    <tr>
                      <th>定額取り崩し</th>
                      <td>保有資産が0円となる時点における年齢</td>
                    </tr>
                  </tbody>
                </table>
                <p>
                  *1
                  シミュレーション結果は1万円未満を四捨五入し、万円単位で表示。各年齢の開始時点の値。
                </p>
                <p className="u-mb1em">*2 120歳を超える場合は「120＋歳---ヶ月」と表示</p>
                <p className="u-mb1em">
                  PCではグラフをマウスオーバーあるいはクリック、スマートフォンではグラフをタップすると、各時点の年齢における「運用した場合」「運用しない場合」それぞれの計算結果を表示します。
                </p>

                <h3 className="c-heading-1">シミュレーションの計算方法</h3>
                <p>以下の式より保有資産残高(X)を求めます。</p>
                <p className="u-mb1em u-mt1em">
                  <img
                    className="torikuzushi_formula_pc"
                    alt="シミュレーションの計算方法"
                    src={guide_reversal_1}
                  />
                </p>
                <p className="u-mb1em u-mt1em">
                  <img
                    className="torikuzushi_formula_sp"
                    alt="シミュレーションの計算方法"
                    src={guide_reversal_1_sp}
                  />
                </p>
                <p className="u-mb1em">
                  *「当月の取り崩し額」は取り崩し頻度や取り崩し方法によってそれぞれ以下のように計算されます。
                </p>
                <h4>毎月での取り崩し</h4>
                <ul className="u-mb1em add-style">
                  <li>定額取り崩し：入力画面にてご入力いただいた「定額（1回あたり）」の金額</li>
                  <li>定率取り崩し：前月末残高 × 取り崩し率（年間）÷12</li>
                </ul>
                <h4>隔月での取り崩し</h4>
                <ol className="add-style">
                  <li>
                    取り崩し月<sup>*</sup>の場合
                    <ul className="add-style">
                      <li>定額取り崩し：入力画面にてご入力いただいた「定額（1回あたり）」の金額</li>
                      <li>定率取り崩し：前月末残高 × 取り崩し率（年間）÷6</li>
                    </ul>
                  </li>
                  <li>
                    取り崩し月でない場合<br></br>定額・定率ともに0円
                  </li>
                </ol>
                <p>
                  * シミュレーションでは、開始時点から起算して偶数月を取り崩し月としております。
                </p>

                <h3 className="c-heading-1">用語説明</h3>
                <h4>参考情報</h4>
                <dl className="c-modal__d-list">
                  <dt>リターン</dt>
                  <dd>
                    リターンとは、投資収益率のことで、3年、5年など、決められた2つの時点の価格（指数値）を比較して、何％上昇したか（あるいは下落したか）を表す指標のことです。
                  </dd>
                </dl>
              </div>
            </div>
            <div className="c-modal__overlay" onClick={handleHide}></div>
          </Modal>
        )}
      </React.Fragment>
    )
  })
